/**
 * Pure value-derivation helpers for MemoForm's due-date and content fields.
 * No JSX, no React state — extracted from MemoForm.tsx so the contract each
 * function implements (documented per-function below) is independently
 * testable and importable without pulling in the whole form.
 *
 * `combineDue`'s exact contract is already relied on informally elsewhere —
 * see the comment in `pages/calendar/utils/eventMapper.ts` documenting the
 * shape it emits.
 */

import {
  parseAbsoluteToLocal,
  toCalendarDate,
  toTime,
} from '@internationalized/date';
import DOMPurify from 'dompurify';
import type { TimeValue } from 'react-aria-components';

import type { MemoEntityType, MemoPrivacy } from './notes.utils';
import { memoPrivacy } from './notes.utils';
import type { Note } from './useMemoRecords';

/**
 * Derive the form's initial date + time from a note. When the note has a
 * precise `datetime` (ahoy_datetime), split it into a calendar date string and
 * a time value; otherwise fall back to the date-only value (or the create-mode
 * `initialDate` prefill). Returns `time: null` when there's no time component.
 */
export function computeInitialDue(
  note: Pick<Note, 'date' | 'datetime'> | undefined,
  initialDate: string | undefined,
  mode: 'create' | 'edit'
): { date: string; time: TimeValue | null } {
  if (note?.datetime) {
    try {
      const absolute = parseAbsoluteToLocal(note.datetime);
      return {
        date: toCalendarDate(absolute).toString(),
        time: toTime(absolute),
      };
    } catch {
      // Malformed datetime — fall through to the date-only path.
    }
  }
  // Create mode may receive a full ISO datetime as `initialDate` (e.g. from a
  // click on a timed calendar slot). Split it into a calendar date + time,
  // mirroring the `note.datetime` branch above. A plain YYYY-MM-DD passes
  // through unchanged as a date-only due.
  if (mode === 'create' && initialDate?.includes('T')) {
    try {
      const absolute = parseAbsoluteToLocal(initialDate);
      return {
        date: toCalendarDate(absolute).toString(),
        time: toTime(absolute),
      };
    } catch {
      // Malformed datetime — fall through to the date-only path.
    }
  }
  const date = note?.date ?? (mode === 'create' ? (initialDate ?? '') : '');
  return { date, time: null };
}

/**
 * Whether the note body is semantically empty. The Plate editor always renders
 * at least one paragraph, so an untouched or cleared note serializes to
 * `<p></p>` (or `<p><br></p>`) rather than `''`. Strip tags + whitespace before
 * deciding emptiness — mirrors MarkdownInput's handleChange so "empty" means
 * the same thing across note editors. Mention tags carry their display name as
 * text content, so a note containing only a mention still reads as non-empty.
 *
 * Without this, the editor's on-mount `<p></p>` emit makes a brand-new note
 * look edited: the dirty guard prompts "discard?" and the save button enables,
 * both for a note the user never touched.
 */
export function isContentEmpty(content: string): boolean {
  // Stripping tags leaves HTML whitespace ENTITIES as literal text (DOMPurify
  // with ALLOWED_TAGS:[] does not decode them), so `<p>&nbsp;</p>` survives as
  // the string "&nbsp;" and `.trim()` sees it as non-empty. Decode the common
  // non-breaking-space forms to a real space first so visually-blank pasted
  // content (e.g. a single &nbsp;) correctly reads as empty.
  return (
    DOMPurify.sanitize(content, { ALLOWED_TAGS: [] })
      .replace(/&(?:nbsp|#160|#xA0);/gi, ' ')
      .trim() === ''
  );
}

/**
 * Combine the form's date string + optional time into the single "due" value
 * passed to onSubmit: a full ISO datetime when a time is set (stored on
 * ahoy_datetime downstream), a plain YYYY-MM-DD date when not (stored on
 * ahoy_date), or undefined when there's no date.
 */
export function combineDue(
  date: string,
  time: TimeValue | null
): string | undefined {
  if (!date) {
    return undefined;
  }
  if (!time) {
    return date;
  }
  const [year, month, day] = date.split('-').map(Number);
  // Preserve the source time's seconds/fractional seconds. Hardcoding 0, 0 here
  // truncated any non-zero seconds/millis on an existing ahoy_datetime, so
  // editing only a memo's title/body produced a different ISO string and the
  // update mutation (exact-string compare) wrote the truncated due back.
  return new Date(
    year,
    month - 1,
    day,
    time.hour,
    time.minute,
    time.second ?? 0,
    time.millisecond ?? 0
  ).toISOString();
}

/** The editable fields MemoForm compares against the saved memo. */
export interface MemoEditDirtyParams {
  mode: 'create' | 'edit';
  note: Note | undefined;
  entityType: MemoEntityType;
  title: string;
  content: string;
  date: string;
  time: TimeValue | null;
  ownerEntityId: string;
  privacy: MemoPrivacy;
  /**
   * Pending stage selection (tasks only). `undefined` means "no override yet
   * — the saved stage stands".
   */
  stagePickerOverride: string | null | undefined;
}

/**
 * Edit mode only: has the user actually changed anything? Compares every
 * editable field (title, body, due date/time, owner, stage, privacy) against
 * the saved memo. MemoForm uses it so that clicking Save on an untouched memo
 * just closes the editor (like Cancel) instead of firing a no-op save that
 * submits no events and leaves the form stuck open, and so the inline
 * autosave has nothing to persist.
 *
 * Errs toward "dirty": any field that differs — or a create-mode / note-less
 * case we can't compare — returns true, so a real edit is NEVER silently
 * dropped. The only fuzzy field is the body (Plate re-serialization), and a
 * false "dirty" there just falls back to the old no-op-save behavior, never
 * data loss.
 */
export function isMemoEditDirty({
  mode,
  note,
  entityType,
  title,
  content,
  date,
  time,
  ownerEntityId,
  privacy,
  stagePickerOverride,
}: MemoEditDirtyParams): boolean {
  if (mode !== 'edit' || !note) {
    return true;
  }
  if (title.trim() !== (note.title ?? '').trim()) {
    return true;
  }
  const savedContent = note.content ?? '';
  const bothEmpty = isContentEmpty(content) && isContentEmpty(savedContent);
  if (!bothEmpty && content !== savedContent) {
    return true;
  }
  const savedDue = computeInitialDue(note, undefined, 'edit');
  if (date !== savedDue.date) {
    return true;
  }
  const timeChanged =
    (time == null) !== (savedDue.time == null) ||
    (time != null &&
      savedDue.time != null &&
      (time.hour !== savedDue.time.hour ||
        time.minute !== savedDue.time.minute));
  if (timeChanged) {
    return true;
  }
  if ((ownerEntityId || '') !== (note.owner_entity_id ?? '')) {
    return true;
  }
  if (privacy !== memoPrivacy(note)) {
    return true;
  }
  // For tasks: a stage picker selection overrides the derived stage.
  if (entityType === 'ahoy_task') {
    const savedStage = note.stage_entity_id ?? null;
    const effectiveStage =
      stagePickerOverride !== undefined ? stagePickerOverride : savedStage;
    if (effectiveStage !== savedStage) {
      return true;
    }
  }
  return false;
}
